/**
 * Copyright 2025 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
'use memo';
import React, { createContext, useContext, useState } from 'react';

type ProductInstanceId = string;

type LocalCartContextType = {
  addItem: (skuId: string, uniqueId: string) => void;
  isLocallyAdded: (skuId: string, uniqueId: string) => boolean;

  markItemBeingAdded: (skuId: string, uniqueId: string) => void;
  unmarkItemBeingAdded: (skuId: string, uniqueId: string) => void;
  isItemBeingAdded: (skuId: string, uniqueId: string) => boolean;
  isAnyItemBeingAdded: boolean;
  isEmpty: boolean;
};

const LocalCartContext = createContext<LocalCartContextType | undefined>(undefined);

export const LocalCartProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [locallyAddedItems, setItems] = useState<Set<ProductInstanceId>>(new Set());
  const [itemsBeingAdded, setItemsBeingAdded] = useState<Set<ProductInstanceId>>(new Set());

  const generateId = (skuId: string, uniqueId: string) => `${skuId}::${uniqueId}`;

  const addItem = (skuId: string, uniqueId: string) => {
    setItems((prev) => new Set(prev).add(generateId(skuId, uniqueId)));
  };

  const isEmpty = locallyAddedItems.size === 0;

  const isAnyItemBeingAdded = itemsBeingAdded.size > 0;

  const isLocallyAdded = (skuId: string, uniqueId: string) => {
    return locallyAddedItems.has(generateId(skuId, uniqueId));
  };

  const markItemBeingAdded = (skuId: string, uniqueId: string) => {
    setItemsBeingAdded((prev) => new Set(prev).add(generateId(skuId, uniqueId)));
  };

  const unmarkItemBeingAdded = (skuId: string, uniqueId: string) => {
    setItemsBeingAdded((prev) => {
      const newSet = new Set(prev);
      newSet.delete(generateId(skuId, uniqueId));
      return newSet;
    });
  };

  const isItemBeingAdded = (skuId: string, uniqueId: string) => {
    return itemsBeingAdded.has(generateId(skuId, uniqueId));
  };

  const contextValue: LocalCartContextType = {
    addItem,
    isLocallyAdded,
    markItemBeingAdded,
    unmarkItemBeingAdded,
    isItemBeingAdded,
    isAnyItemBeingAdded,
    isEmpty,
  };

  return <LocalCartContext.Provider value={contextValue}>{children}</LocalCartContext.Provider>;
};

export const useLocalCart = (): LocalCartContextType => {
  const ctx = useContext(LocalCartContext);
  if (!ctx) {
    throw new Error('useLocalCart must be used within LocalCartProvider');
  }
  return ctx;
};
